import React, { useCallback, useEffect, useMemo, useState } from 'react';
// TODO: If Runium uses custom JWT auth instead of Clerk, replace useAuth with custom hook
import { useAuth, useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';

import { getApiUrl } from '../../utils/apiUtils';

// Admin whitelist check (matches AdminFeedbackPage pattern)
const RAW_ALLOWLIST = (process.env.REACT_APP_ADMIN_EMAIL_WHITELIST || '')
  .split(',')
  .map((value) => value.trim().toLowerCase())
  .filter(Boolean);

const ADMIN_EMAILS = RAW_ALLOWLIST.filter((entry) => entry.includes('@'));
const ADMIN_IDS = RAW_ALLOWLIST.filter((entry) => !entry.includes('@'));

const PAGE_SIZE = 10;

// Default evaluation suites - fallback if API fails
const DEFAULT_SUITES = [
  { id: '9204eb9a', name: 'Run 1 - Dec 2024' },
  { id: 'abc123', name: 'Run 2 - Training Focus' },
  { id: 'def456', name: 'Run 3 - Mixed Categories' },
];

const ScoreBadge = ({ score }) => {
  const percentage = (score * 100).toFixed(1);
  const getColor = () => {
    if (score >= 0.8) return 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40';
    if (score >= 0.6) return 'bg-amber-500/15 text-amber-200 border-amber-500/40';
    return 'bg-red-500/15 text-red-200 border-red-500/40';
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${getColor()}`}>
      {percentage}%
    </span>
  );
};

const CategoryBadge = ({ category }) => {
  const colors = {
    equipment: 'bg-blue-500/15 text-blue-200 border-blue-500/40',
    nutrition_advice: 'bg-purple-500/15 text-purple-200 border-purple-500/40',
    training: 'bg-green-500/15 text-green-200 border-green-500/40',
    injury_prevention: 'bg-orange-500/15 text-orange-200 border-orange-500/40',
    safety: 'bg-yellow-500/15 text-yellow-200 border-yellow-500/40',
  };
  const color = colors[category] || 'bg-gray-500/15 text-gray-300 border-gray-500/40';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${color}`}>
      {category?.replace('_', ' ') || 'Unknown'}
    </span>
  );
};

const MetricsCard = ({ label, value, subtitle }) => (
  <div className="rounded-xl border border-gray-800 bg-black/20 p-4">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-2xl font-semibold text-white mt-1">{value}</p>
    {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
  </div>
);

const ScoreDistributionBar = ({ label, count, total, percentage }) => {
  const barWidth = total > 0 ? (count / total) * 100 : 0;
  const safePercentage = percentage ?? 0;
  const getBarColor = () => {
    if (safePercentage >= 80) return 'bg-emerald-400';
    if (safePercentage >= 60) return 'bg-amber-400';
    return 'bg-red-400';
  };
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-300">{label}</span>
        <span className="font-semibold text-white">{count} ({safePercentage.toFixed(1)}%)</span>
      </div>
      <div className="w-full bg-gray-700 rounded-full h-2">
        <div className={`${getBarColor()} h-2 rounded-full transition-all duration-300`} style={{ width: `${barWidth}%` }} />
      </div>
    </div>
  );
};

const DetailModal = ({ result, onClose }) => {
  if (!result) return null;

  const score = result.evaluation?.score ?? 0;
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-gray-900 rounded-2xl border border-gray-800 shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-gray-800 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-white">Evaluation Details</h2>
            <p className="text-sm text-gray-400">Case #{result.case_index}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-4 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Overall Score</p>
              <ScoreBadge score={score} />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Correctness</p>
              <p className="text-lg font-semibold text-white">{((result.evaluation?.correctness ?? 0) * 100).toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Relevance</p>
              <p className="text-lg font-semibold text-white">{((result.evaluation?.relevance ?? 0) * 100).toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Guidelines</p>
              <p className="text-lg font-semibold text-white">{((result.evaluation?.guidelines_adherence ?? 0) * 100).toFixed(1)}%</p>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Category</p>
            <CategoryBadge category={result.case_metadata?.category} />
            <p className="text-sm text-gray-400 mt-2">
              Expected Quality: <span className="capitalize">{result.case_metadata?.expected_quality || 'N/A'}</span>
            </p>
          </div>

          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Coach Response</p>
            <div className="bg-gray-800/50 rounded-lg p-4 text-sm text-gray-200 whitespace-pre-wrap">
              {result.coach_response || 'No response available'}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Evaluation Reasoning</p>
            <div className="bg-gray-800/50 rounded-lg p-4 text-sm text-gray-200">
              {result.evaluation?.reasoning || 'No reasoning provided'}
            </div>
          </div>

          {result.evaluation?.issues && result.evaluation.issues.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Issues Found</p>
              <ul className="space-y-2">
                {result.evaluation.issues.map((issue, idx) => (
                  <li key={idx} className="bg-red-900/20 border border-red-500/40 rounded-lg p-3 text-sm text-red-200">
                    {issue}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Integration Status</p>
            <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${
              result.integration_status === 'ok'
                ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40'
                : 'bg-red-500/15 text-red-200 border-red-500/40'
            }`}>
              {result.integration_status || 'Unknown'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const EvaluationDashboardBackup = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { user } = useUser();
  const primaryEmail = user?.primaryEmailAddress?.emailAddress?.toLowerCase();
  const clerkId = user?.id?.toLowerCase();

  // Admin whitelist check
  const isConfigured = RAW_ALLOWLIST.length > 0;
  const isAuthorized = useMemo(() => {
    if (!isConfigured || !(primaryEmail || clerkId)) {
      return false;
    }
    return ADMIN_EMAILS.includes(primaryEmail) || ADMIN_IDS.includes(clerkId);
  }, [primaryEmail, clerkId, isConfigured]);

  const [selectedSuiteId, setSelectedSuiteId] = useState('9204eb9a'); // Default suite
  const [availableSuites, setAvailableSuites] = useState(DEFAULT_SUITES);
  const [evaluationData, setEvaluationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    expected_quality: '',
    minScore: 0,
    maxScore: 1,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'case_index', direction: 'asc' });
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);

  const loadEvaluationData = useCallback(async (suiteId) => {
    setLoading(true);
    setError('');
    setCurrentPage(1);

    try {
      // Try API endpoint first
      const apiUrl = getApiUrl();
      const token = await getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      let response = await fetch(`${apiUrl}/admin/evaluation/results?suite_id=${suiteId}`, { headers });
      
      // Fallback to static file if API fails
      if (!response.ok) {
        console.warn(`API endpoint failed (${response.status}), falling back to static file`);
        response = await fetch(`/evaluation_results_${suiteId}.json`);
        if (!response.ok) {
          throw new Error(`Failed to load evaluation data: ${response.statusText}`);
        }
      }
      
      const data = await response.json();
      setEvaluationData(data);
      setLastFetchedAt(new Date().toISOString());
    } catch (fetchError) {
      setError(fetchError.message || 'Unexpected error loading evaluation data.');
      console.error('Error loading evaluation data:', fetchError);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const loadAvailableSuites = useCallback(async () => {
    try {
      const apiUrl = getApiUrl();
      const token = await getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${apiUrl}/admin/evaluation/suites`, { headers });
      
      if (response.ok) {
        const suites = await response.json();
        // Ensure suites is always an array
        if (Array.isArray(suites)) {
          setAvailableSuites(suites);
        } else {
          console.warn('API returned non-array response, using defaults:', suites);
          // Keep DEFAULT_SUITES (already set in state)
        }
      } else {
        // Fallback to default suites if API fails
        console.warn('Failed to load suites from API, using defaults');
      }
    } catch (err) {
      console.warn('Error loading suites from API, using defaults:', err);
    }
  }, [getToken]);

  useEffect(() => {
    loadAvailableSuites();
  }, [loadAvailableSuites]);

  useEffect(() => {
    loadEvaluationData(selectedSuiteId);
  }, [selectedSuiteId, loadEvaluationData]);

  const filteredResults = useMemo(() => {
    if (!evaluationData?.results) return [];
    let filtered = [...evaluationData.results];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((result) => {
        const response = result.coach_response?.toLowerCase() || '';
        const reasoning = result.evaluation?.reasoning?.toLowerCase() || '';
        const category = result.case_metadata?.category?.toLowerCase() || '';
        return response.includes(query) || reasoning.includes(query) || category.includes(query);
      });
    }
    if (filters.category) {
      filtered = filtered.filter((result) => result.case_metadata?.category === filters.category);
    }
    if (filters.expected_quality) {
      filtered = filtered.filter((result) => result.case_metadata?.expected_quality === filters.expected_quality);
    }
    filtered = filtered.filter((result) => {
      const score = result.evaluation?.score ?? 0;
      return score >= filters.minScore && score <= filters.maxScore;
    });
    filtered.sort((a, b) => {
      let aValue, bValue;
      switch (sortConfig.key) {
        case 'score':
          aValue = a.evaluation?.score ?? 0;
          bValue = b.evaluation?.score ?? 0;
          break;
        case 'category':
          aValue = a.case_metadata?.category || '';
          bValue = b.case_metadata?.category || '';
          break;
        default:
          aValue = a.case_index ?? 0;
          bValue = b.case_index ?? 0;
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [evaluationData, searchQuery, filters, sortConfig]);

  const paginatedResults = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredResults.slice(start, start + PAGE_SIZE);
  }, [filteredResults, currentPage]);

  const analytics = useMemo(() => {
    if (!evaluationData?.metrics) {
      return {
        overall: { total_cases: 0, avg_score: 0, min_score: 0, max_score: 0, pass_rate: 0 },
        score_distribution: { high: { count: 0, percentage: 0 }, medium: { count: 0, percentage: 0 }, low: { count: 0, percentage: 0 } },
        by_category: {},
      };
    }
    const metrics = evaluationData.metrics;
    const overall = metrics.overall || {};
    const scoreDist = metrics.score_distribution || {};
    const byCategory = metrics.by_category || {};
    const totalResults = filteredResults.length;
    const passingResults = filteredResults.filter((r) => (r.evaluation?.score ?? 0) >= 0.8).length;
    const passRate = totalResults > 0 ? (passingResults / totalResults) * 100 : 0;
    return {
      overall: {
        total_cases: overall.total_cases || totalResults,
        avg_score: overall.avg_score || 0,
        min_score: overall.min_score || 0,
        max_score: overall.max_score || 0,
        pass_rate: passRate,
      },
      score_distribution: {
        high: scoreDist.high || { count: 0, percentage: 0 },
        medium: scoreDist.medium || { count: 0, percentage: 0 },
        low: scoreDist.low || { count: 0, percentage: 0 },
      },
      by_category: byCategory,
    };
  }, [evaluationData, filteredResults]);

  const handleFilterChange = (key, value) => {
    setCurrentPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleExport = useCallback(
    (format) => {
      if (!filteredResults.length || typeof document === 'undefined') return;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      if (format === 'json') {
        const payload = JSON.stringify(
          { suite: evaluationData?.suite, results: filteredResults, metrics: analytics, exported_at: new Date().toISOString() },
          null,
          2
        );
        const blob = new Blob([payload], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `evaluation_export_${timestamp}.json`;
        link.click();
        URL.revokeObjectURL(url);
        return;
      }
      // CSV export with UTF-8 BOM for proper encoding
      const escapeCsv = (value) => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };
      const headers = ['Case Index', 'Category', 'Expected Quality', 'Score', 'Correctness', 'Relevance', 'Guidelines Adherence', 'Has Issues', 'Integration Status'];
      const rows = filteredResults.map((result) => [
        result.case_index || '',
        escapeCsv(result.case_metadata?.category || ''),
        escapeCsv(result.case_metadata?.expected_quality || ''),
        (result.evaluation?.score ?? 0).toFixed(3),
        (result.evaluation?.correctness ?? 0).toFixed(3),
        (result.evaluation?.relevance ?? 0).toFixed(3),
        (result.evaluation?.guidelines_adherence ?? 0).toFixed(3),
        (result.evaluation?.issues?.length > 0 ? 'Yes' : 'No'),
        escapeCsv(result.integration_status || ''),
      ]);
      const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((cells) => cells.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `evaluation_export_${timestamp}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    [filteredResults, evaluationData, analytics]
  );

  const categories = useMemo(() => {
    if (!evaluationData?.results) return [];
    const cats = new Set();
    evaluationData.results.forEach((r) => {
      if (r.case_metadata?.category) cats.add(r.case_metadata.category);
    });
    return Array.from(cats).sort();
  }, [evaluationData]);

  const totalPages = Math.ceil(filteredResults.length / PAGE_SIZE);

  // Admin access check - show error if not authorized
  if (!isConfigured) {
    return (
      <div className="min-h-screen bg-gray-950 text-gray-200 flex items-center justify-center px-6">
        <div className="max-w-xl space-y-4 text-center">
          <p className="text-2xl font-semibold text-white">Admin access not configured</p>
          <p className="text-gray-400">
            Set <code className="text-blue-300">REACT_APP_ADMIN_EMAIL_WHITELIST</code> to a comma-separated list of admin
            emails so the admin console can enforce access controls.
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-950 text-gray-200 flex items-center justify-center px-6">
        <div className="max-w-lg space-y-4 text-center">
          <p className="text-2xl font-semibold text-white">Restricted area</p>
          <p className="text-gray-400">
            Your account is not in the admin allowlist. If you believe this is an error, please contact the Runium team.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      <div className="mx-auto w-full max-w-7xl px-6 py-10 space-y-8">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm uppercase tracking-widest text-blue-400">Admin</p>
            <h1 className="text-3xl font-semibold text-white">LLM Evaluation Dashboard</h1>
            <p className="text-sm text-gray-400">Monitor and analyze LLM-as-Judge evaluation results in real-time.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <label className="text-xs uppercase tracking-wide text-gray-500 block mb-1">Suite</label>
              <select
                value={selectedSuiteId}
                onChange={(e) => setSelectedSuiteId(e.target.value)}
                className="rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.isArray(availableSuites) && availableSuites.map((suite) => (
                  <option key={suite.id} value={suite.id}>
                    {suite.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex items-center rounded-lg border border-gray-800 bg-black/30 px-3 py-2 text-xs font-medium text-gray-200 hover:text-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to Coach
            </button>
            {lastFetchedAt && (
              <span className="text-xs text-gray-500">
                Updated {new Date(lastFetchedAt).toLocaleTimeString(undefined, { timeStyle: 'short' })}
              </span>
            )}
            <button
              type="button"
              onClick={() => loadEvaluationData(selectedSuiteId)}
              className="inline-flex items-center rounded-lg border border-blue-500/40 bg-blue-500/10 px-4 py-2 text-sm font-medium text-blue-200 hover:bg-blue-500/20 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={() => {/* TODO: Implement multi-suite comparison */}}
              className="rounded-lg border border-gray-800 px-3 py-1.5 text-xs font-medium text-gray-200 hover:border-blue-500 hover:text-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 opacity-50 cursor-not-allowed"
              disabled
              title="Coming soon: Compare multiple suites"
            >
              Compare Suites
            </button>
            <button
              type="button"
              onClick={() => handleExport('csv')}
              disabled={!filteredResults.length}
              className="rounded-lg border border-gray-800 px-3 py-1.5 text-xs font-medium text-gray-200 hover:border-blue-500 hover:text-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Export CSV
            </button>
            <button
              type="button"
              onClick={() => handleExport('json')}
              disabled={!filteredResults.length}
              className="rounded-lg border border-gray-800 px-3 py-1.5 text-xs font-medium text-gray-200 hover:border-blue-500 hover:text-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Export JSON
            </button>
          </div>
        </header>

        <section className="rounded-2xl border border-gray-900 bg-gray-900/60 p-4 shadow-lg">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <label className="text-xs uppercase tracking-wide text-gray-500">Search</label>
              <input
                type="text"
                placeholder="Search responses, reasoning, or category"
                className="mt-1 w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-gray-500">Category</label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-gray-500">Expected Quality</label>
              <select
                value={filters.expected_quality}
                onChange={(e) => handleFilterChange('expected_quality', e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="high">High</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-gray-500">Min Score</label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.1"
                value={filters.minScore}
                onChange={(e) => handleFilterChange('minScore', parseFloat(e.target.value) || 0)}
                className="mt-1 w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </section>

        {!loading && evaluationData && (
          <section className="rounded-2xl border border-gray-900 bg-gray-900/50 p-5 shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-800 pb-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500">Metrics</p>
                <h2 className="text-lg font-semibold text-white">Overall Performance</h2>
              </div>
            </div>
            <div className="mt-5 grid gap-4 lg:grid-cols-4">
              <MetricsCard label="Total Cases" value={analytics.overall.total_cases} subtitle={`${filteredResults.length} after filters`} />
              <MetricsCard
                label="Average Score"
                value={((analytics.overall.avg_score || 0) * 100).toFixed(1) + '%'}
                subtitle={`Range: ${((analytics.overall.min_score || 0) * 100).toFixed(1)}% - ${((analytics.overall.max_score || 0) * 100).toFixed(1)}%`}
              />
              <MetricsCard label="Pass Rate" value={(analytics.overall.pass_rate ?? 0).toFixed(1) + '%'} subtitle="Scores ≥ 80%" />
              <MetricsCard
                label="Success Rate"
                value={
                  analytics.overall.total_cases > 0
                    ? (((analytics.overall.total_cases - (evaluationData.metrics?.overall?.failed_cases || 0)) / analytics.overall.total_cases) * 100).toFixed(1) + '%'
                    : '0%'
                }
                subtitle="Successful evaluations"
              />
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">Score Distribution</p>
                <div className="space-y-3">
                  <ScoreDistributionBar label="High (≥80%)" count={analytics.score_distribution.high.count} total={analytics.overall.total_cases} percentage={analytics.score_distribution.high.percentage} />
                  <ScoreDistributionBar label="Medium (50-79%)" count={analytics.score_distribution.medium.count} total={analytics.overall.total_cases} percentage={analytics.score_distribution.medium.percentage} />
                  <ScoreDistributionBar label="Low (<50%)" count={analytics.score_distribution.low.count} total={analytics.overall.total_cases} percentage={analytics.score_distribution.low.percentage} />
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">By Category</p>
                <ul className="space-y-2 text-sm text-gray-200">
                  {Object.entries(analytics.by_category).length > 0 ? (
                    Object.entries(analytics.by_category).map(([category, data]) => (
                      <li key={category} className="flex items-center justify-between rounded-lg border border-gray-900 bg-black/20 px-3 py-2">
                        <span className="capitalize">{category.replace('_', ' ')}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-white">{data.count}</span>
                          <span className="text-xs text-gray-400">({((data.avg_score || 0) * 100).toFixed(1)}%)</span>
                        </div>
                      </li>
                    ))
                  ) : (
                    <li className="text-gray-500 text-xs">No category data available.</li>
                  )}
                </ul>
              </div>
            </div>
          </section>
        )}

        {error && <div className="rounded-xl border border-red-500/40 bg-red-900/20 p-4 text-sm text-red-100">{error}</div>}
        {loading && <div className="rounded-xl border border-gray-900 bg-gray-900/40 p-10 text-center text-gray-400">Loading evaluation data...</div>}
        {!loading && !error && (!evaluationData || filteredResults.length === 0) && (
          <div className="rounded-xl border border-gray-900 bg-gray-900/40 p-10 text-center text-gray-500">
            {!evaluationData ? 'No evaluation data available.' : 'No results found for the selected filters.'}
          </div>
        )}

        {!loading && !error && filteredResults.length > 0 && (
          <section className="rounded-2xl border border-gray-900 bg-gray-900/40 shadow-xl">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-900 text-sm">
                <thead className="bg-gray-900/60">
                  <tr className="text-left text-xs uppercase tracking-widest text-gray-400">
                    <th className="px-4 py-3 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('case_index')}>
                      ID {sortConfig.key === 'case_index' && <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                    <th className="px-4 py-3 font-medium">Category</th>
                    <th className="px-4 py-3 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('score')}>
                      Score {sortConfig.key === 'score' && <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                    <th className="px-4 py-3 font-medium">Correctness</th>
                    <th className="px-4 py-3 font-medium">Relevance</th>
                    <th className="px-4 py-3 font-medium">Guidelines</th>
                    <th className="px-4 py-3 font-medium">Response Preview</th>
                    <th className="px-4 py-3 font-medium">Issues</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-900">
                  {paginatedResults.map((result) => {
                    const score = result.evaluation?.score ?? 0;
                    const responsePreview = result.coach_response?.substring(0, 100) || '';
                    const hasIssues = (result.evaluation?.issues?.length || 0) > 0;
                    return (
                      <tr
                        key={result.case_index}
                        className="bg-transparent transition hover:bg-blue-500/5 cursor-pointer"
                        onClick={() => setSelectedResult(result)}
                      >
                        <td className="px-4 py-3 font-mono text-xs text-gray-400">#{result.case_index}</td>
                        <td className="px-4 py-3"><CategoryBadge category={result.case_metadata?.category} /></td>
                        <td className="px-4 py-3"><ScoreBadge score={score} /></td>
                        <td className="px-4 py-3 text-gray-300">{((result.evaluation?.correctness ?? 0) * 100).toFixed(1)}%</td>
                        <td className="px-4 py-3 text-gray-300">{((result.evaluation?.relevance ?? 0) * 100).toFixed(1)}%</td>
                        <td className="px-4 py-3 text-gray-300">{((result.evaluation?.guidelines_adherence ?? 0) * 100).toFixed(1)}%</td>
                        <td className="px-4 py-3 text-white max-w-md">
                          <p className="truncate text-sm">{responsePreview}</p>
                          {result.evaluation?.reasoning && <p className="text-xs text-gray-500 mt-1 truncate">{result.evaluation.reasoning.substring(0, 80)}...</p>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {hasIssues ? (
                            <span className="text-xs text-red-300">
                              {result.evaluation.issues.length} issue{result.evaluation.issues.length !== 1 ? 's' : ''}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500">None</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between border-t border-gray-900 px-4 py-3 text-xs text-gray-400">
              <p>Page {currentPage} of {totalPages} · {filteredResults.length} total results</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="rounded-lg border border-gray-800 px-3 py-1 text-gray-300 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="rounded-lg border border-gray-800 px-3 py-1 text-gray-300 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </section>
        )}
      </div>
      {selectedResult && <DetailModal result={selectedResult} onClose={() => setSelectedResult(null)} />}
    </div>
  );
};

export default EvaluationDashboardBackup;
